import React, { useEffect, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { chevronForwardOutline, closeOutline } from 'ionicons/icons';
import {
  ICalificacionHistorial,
  IDetalleHistorialFiscalizacion,
  IHistorialFiscalizacion,
  fetchDetalleHistorialFiscalizacion,
  fetchHistorialFiscalizaciones,
} from 'src/services/Fiscalizacion/Fiscalizacion';
import '../../pages/FiscalizacionReel.css';
import './HistorialFiscalizaciones.css';

interface Props {
  psId?: number;
}

// "2026-10-14 09:30" -> "14/10/2026 09:30"
const formatearFecha = (fecha?: string | null) => {
  if (!fecha) return '-';
  const [dia, hora] = fecha.split(' ');
  const [a, m, d] = dia.split('-');
  return d ? `${d}/${m}/${a}${hora ? ` ${hora.slice(0, 5)}` : ''}` : fecha;
};

const valor = (v: unknown) => (v === null || v === undefined || v === '' ? '-' : String(v));

const Seccion: React.FC<{ titulo: string; icono: string; vacio: boolean; comentarios?: string[] }> = ({
  titulo, icono, vacio, comentarios = [], children,
}) => (
  <section className="histFis-seccion">
    <div className="histFis-seccion-titulo">
      <img src={icono} alt="" />
      <h5>{titulo}</h5>
    </div>
    {vacio ? comentarios.length === 0 && <p className="histFis-vacio">Sin datos cargados</p> : children}
    {comentarios.map((c, i) => (
      <p key={i} className="histFis-comentario"><strong>Comentario:</strong> {c}</p>
    ))}
  </section>
);

const Campo: React.FC<{ label: string; ancho?: boolean }> = ({ label, ancho, children }) => (
  <div className={`histFis-campo${ancho ? ' histFis-campo--ancho' : ''}`}>
    <span>{label}</span>
    <div>{children}</div>
  </div>
);

const Tarjeta: React.FC<{ titulo?: string; sub?: string; compacta?: boolean }> = ({ titulo, sub, compacta, children }) => (
  <div className="histFis-tarjeta">
    {titulo && (
      <div className="histFis-tarjeta-titulo">
        {titulo}
        {sub && <span className="histFis-sub"> · {sub}</span>}
      </div>
    )}
    <div className={`histFis-campos${compacta ? ' histFis-campos--compactos' : ''}`}>{children}</div>
  </div>
);

const Calificacion: React.FC<{ c: ICalificacionHistorial | null }> = ({ c }) => {
  if (!c) return <>-</>;
  const extra = [c.medida_correctiva, c.plazo_implementacion && `plazo ${formatearFecha(c.plazo_implementacion)}`]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      {valor(c.calificacion)}
      {extra && <div className="histFis-sub">{extra}</div>}
    </>
  );
};

const DetalleFiscalizacion: React.FC<{ detalle: IDetalleHistorialFiscalizacion; fecha: string }> = ({ detalle, fecha }) => {
  const { personal, deposito, maquinas, limpieza_general, lavado_profundo, opinion_cliente, stock, comentarios } = detalle;

  return (
    <div className="histFis-detalle">
      <div className="histFis-campos histFis-cabecera">
        <Campo label="Fecha">{formatearFecha(fecha)}</Campo>
        <Campo label="Objetivo">{valor(detalle.objetivos)}</Campo>
        <Campo label="Fiscal">{valor(detalle.fiscal_nombre)}</Campo>
        <Campo label="Contacto">{valor(detalle.contacto_nombre)}</Campo>
      </div>

      <Seccion titulo="Personal" icono="assets/icon/fisc-COD1.svg" vacio={!personal.length} comentarios={comentarios.personal}>
        {personal.map((op) => (
          <Tarjeta key={op.legajo} titulo={op.nombre}>
            <Campo label="Imagen"><Calificacion c={op.imagen} /></Campo>
            <Campo label="Rutina"><Calificacion c={op.rutina} /></Campo>
            <Campo label="Procedimiento"><Calificacion c={op.procedimiento} /></Campo>
          </Tarjeta>
        ))}
      </Seccion>

      <Seccion titulo="Depósito" icono="assets/icon/fisc-COD2.svg" vacio={!deposito.calificacion} comentarios={comentarios.deposito}>
        <Tarjeta>
          <Campo label="Calificación">{valor(deposito.calificacion)}</Campo>
          <Campo label="Plazo">{formatearFecha(deposito.plazo_implementacion)}</Campo>
          <Campo label="Medida correctiva" ancho>{valor(deposito.medida_correctiva)}</Campo>
        </Tarjeta>
      </Seccion>

      <Seccion titulo="Máquinas" icono="assets/icon/fisc-COD3.svg" vacio={!maquinas.length} comentarios={comentarios.maquinas}>
        {maquinas.map((m) => {
          const problema = m.descripcion_problema !== 'N/A' ? m.descripcion_problema : null;
          return (
            <Tarjeta key={m.id} titulo={valor(m.maquina_descripcion)} sub={m.maquina_codigo}>
              <Campo label="Condición">{valor(m.calificacion)}</Campo>
              {m.medida_correctiva && <Campo label="Medida correctiva">{m.medida_correctiva}</Campo>}
              {problema && <Campo label="Problema" ancho>{problema}</Campo>}
            </Tarjeta>
          );
        })}
      </Seccion>

      <Seccion
        titulo="Nivel de limpieza general"
        icono="assets/icon/fisc-COD4.svg"
        vacio={!limpieza_general.calificacion && !lavado_profundo.length}
        comentarios={comentarios.limpieza}
      >
        {limpieza_general.calificacion && (
          <Tarjeta>
            <Campo label="Calificación">{valor(limpieza_general.calificacion)}</Campo>
            <Campo label="Comentario" ancho>{valor(limpieza_general.comentario)}</Campo>
            {limpieza_general.acciones_correctivas && (
              <Campo label="Acciones correctivas" ancho>{limpieza_general.acciones_correctivas}</Campo>
            )}
          </Tarjeta>
        )}
        {lavado_profundo.map((lp) => (
          <Tarjeta key={lp.id} titulo={`Lavado profundo: ${valor(lp.tipo_lavado)}`}>
            <Campo label="Frecuencia">{valor(lp.frecuencia)}</Campo>
            <Campo label="Estado">{valor(lp.estado)}</Campo>
            <Campo label="Horas">{valor(lp.cant_horas)}</Campo>
          </Tarjeta>
        ))}
      </Seccion>

      <Seccion titulo="Opinión del cliente" icono="assets/icon/fisc-COD5.svg" vacio={!opinion_cliente.comentario}>
        <Tarjeta>
          <Campo label="Contacto" ancho>{valor(opinion_cliente.contacto)}</Campo>
          <Campo label="Opinión" ancho>{valor(opinion_cliente.comentario)}</Campo>
        </Tarjeta>
      </Seccion>

      <Seccion titulo="Stock de productos" icono="assets/icon/fisc-COD6.svg" vacio={!stock.length} comentarios={comentarios.stock}>
        {stock.map((p) => (
          <Tarjeta key={p.id} titulo={valor(p.descripcion)} sub={p.codigo} compacta>
            <Campo label="Stock">{valor(p.cantidad_stock)}</Campo>
            <Campo label="Sugerido">{valor(p.cantidad_sugerida)}</Campo>
            <Campo label="%">{valor(p.porcentaje)}</Campo>
          </Tarjeta>
        ))}
      </Seccion>

      {comentarios.otros.length > 0 && (
        <Seccion titulo="Otros comentarios" icono="assets/icon/fisc-COD5.svg" vacio={false}>
          {comentarios.otros.map((c, i) => <p key={i} className="histFis-comentario">{c}</p>)}
        </Seccion>
      )}
    </div>
  );
};

const HistorialFiscalizaciones: React.FC<Props> = ({ psId }) => {
  const [present] = useIonToast();
  const [historial, setHistorial] = useState<IHistorialFiscalizacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [seleccion, setSeleccion] = useState<IHistorialFiscalizacion | null>(null);
  const [detalle, setDetalle] = useState<IDetalleHistorialFiscalizacion | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  useEffect(() => {
    if (!psId) return;
    setCargando(true);
    fetchHistorialFiscalizaciones(psId).then((res) => {
      if (res.error) present(res.error, 4000);
      setHistorial(res.datos);
      setCargando(false);
    });
  }, [psId]);

  const abrirDetalle = async (fis: IHistorialFiscalizacion) => {
    setSeleccion(fis);
    setDetalle(null);
    setCargandoDetalle(true);
    const res = await fetchDetalleHistorialFiscalizacion(fis.fis_id);
    setCargandoDetalle(false);
    if (res.error || !res.datos) {
      present(res.error || 'No se pudo obtener el detalle', 4000);
      setSeleccion(null);
      return;
    }
    setDetalle(res.datos);
  };

  const cerrarDetalle = () => {
    setSeleccion(null);
    setDetalle(null);
  };

  return (
    <div className="fisReel histFis">
      <div className="fiscalizacion-resumen-header">
        <h4 style={{ margin: 0 }}>Últimas fiscalizaciones</h4>
      </div>

      {cargando ? (
        <div className="histFis-centro"><IonSpinner /></div>
      ) : historial.length === 0 ? (
        <p className="histFis-vacio">Este punto de servicio no tiene fiscalizaciones finalizadas.</p>
      ) : (
        <ul className="histFis-lista">
          {historial.map((fis) => (
            <li key={fis.fis_id}>
              <button type="button" onClick={() => abrirDetalle(fis)}>
                <div className="histFis-item-texto">
                  <strong>{formatearFecha(fis.fecha)}</strong>
                  <span>{fis.objetivo} · {fis.fiscal_nombre}</span>
                </div>
                {fis.limpieza_calificacion && <span className="histFis-badge">{fis.limpieza_calificacion}</span>}
                <IonIcon icon={chevronForwardOutline} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <IonModal isOpen={!!seleccion} onDidDismiss={cerrarDetalle}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Fiscalización {seleccion ? formatearFecha(seleccion.fecha).split(' ')[0] : ''}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={cerrarDetalle} aria-label="Cerrar">
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {cargandoDetalle || !detalle || !seleccion ? (
            <div className="histFis-centro"><IonSpinner /></div>
          ) : (
            <DetalleFiscalizacion detalle={detalle} fecha={seleccion.fecha} />
          )}
        </IonContent>
      </IonModal>
    </div>
  );
};

export default HistorialFiscalizaciones;
